import {LOCALES, type Locale} from '../shared/constants/locales';

export {LOCALE_LABELS, type TextDirection} from '../shared/constants/locales';
export {getTextDirection, isRtlLocale} from '../shared/utils/locale';
export {LOCALES, type Locale};

export const LOCALE_LIST: readonly Locale[] = Object.values(LOCALES);

export const DEFAULT_LOCALE: Locale = LOCALES.ENGLISH;

// Comma-separated locales to take offline without deleting their dictionaries,
// fonts or RTL support (e.g. DISABLED_LOCALES=ar). Deliberately NOT a
// NEXT_PUBLIC_ var: those are inlined at image-build time, so the value could
// never be changed from the ECS task definition. Read inside the function rather
// than at module scope so a server render picks up the current runtime value
// instead of whatever was set when the module was first evaluated.
//
// It follows that the browser bundle always sees every locale as enabled —
// process.env is empty there. Client components must therefore be TOLD which
// locales are enabled by a server component (see Header's `locales` prop); they
// must not call getEnabledLocales() to decide what to render.
function getDisabledLocales(): readonly string[] {
  return (process.env.DISABLED_LOCALES ?? '')
    .split(',')
    .map(value => value.trim())
    .filter(value => value !== '' && value !== DEFAULT_LOCALE);
}

export function getEnabledLocales(): readonly Locale[] {
  const disabled = getDisabledLocales();
  return LOCALE_LIST.filter(locale => !disabled.includes(locale));
}

export function isLocale(value: string): value is Locale {
  return (getEnabledLocales() as readonly string[]).includes(value);
}

export function isDisabledLocale(value: string): boolean {
  return (LOCALE_LIST as readonly string[]).includes(value) && !isLocale(value);
}

// URL contract: the default locale lives at the bare path ("/about"); every
// other locale is prefixed ("/ar/about"). All hrefs must be built through this
// helper — a hand-written `/${locale}/...` reintroduces the "/en" prefix, which
// the proxy serves as is, leaving the visitor on a non-canonical URL.
export function localeHref(locale: string, path = ''): string {
  if (locale === DEFAULT_LOCALE) {
    return path || '/';
  }
  return `/${locale}${path}`;
}

// Splits a pathname into its locale prefix (any known locale, enabled or not —
// callers decide what a disabled one means) and the locale-less remainder, which
// always starts with "/".
export function splitLocalePathname(pathname: string): {locale?: Locale; path: string} {
  const [, prefix = '', ...rest] = pathname.split('/');
  const locale = (LOCALE_LIST as readonly string[]).includes(prefix) ? (prefix as Locale) : undefined;
  if (!locale) {
    return {path: pathname || '/'};
  }
  return {locale, path: rest.length > 0 ? `/${rest.join('/')}` : '/'};
}

export function stripDefaultLocalePrefix(pathname: string): string {
  const {locale, path} = splitLocalePathname(pathname);
  return locale === DEFAULT_LOCALE ? path : pathname;
}
